import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Table,
  Tag,
  Tabs,
  message,
  Spin,
  Modal,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  ArrowLeftOutlined,
  ExportOutlined,
  ImportOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { StoreGroup, Store } from '../../types/storeGroup';
import StoreGroupService from '../../services/storeGroupService';
import { STORE_GROUP_STATUS_COLORS } from '../../mock/storeGroupData';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { confirm } = Modal;

const StoreGroupDetail: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [storeGroup, setStoreGroup] = useState<StoreGroup | null>(null);
  const [stores, setStores] = useState<Store[]>([]);

  const storeGroupService = StoreGroupService;

  useEffect(() => {
    if (code) {
      fetchStoreGroupDetail(code);
    }
  }, [code]);

  const fetchStoreGroupDetail = async (code: string) => {
    setLoading(true);
    try {
      const response = await storeGroupService.getStoreGroupByCode(code);
      if (response.success && response.data) {
        const storeGroup = response.data;
        setStoreGroup(storeGroup);
        
        // 获取门店详情，通过shopCodes获取门店列表
        if (storeGroup.shopCodes && storeGroup.shopCodes.length > 0) {
          console.log('Fetching stores for shopCodes:', storeGroup.shopCodes);
          try {
            // 使用storeGroupService.getStoresByCodes方法获取门店列表
            const storesResponse = await storeGroupService.getStoresByCodes(storeGroup.shopCodes);
            console.log('Stores response:', storesResponse);
            if (storesResponse.success && storesResponse.data) {
              // 转换数据格式
              const allStores = storesResponse.data.map(convertWarehouseToStore);
              const filteredStores = allStores.filter(store => 
                storeGroup.shopCodes.includes(store.code)
              );
              setStores(filteredStores);
            } else {
              console.error('Failed to get stores:', storesResponse.errorMessage);
              message.error(storesResponse.errorMessage || t('storeGroup.fetchStoresFailed'));
              setStores([]);
            }
          } catch (storeError) {
            console.error('Failed to fetch store list:', storeError);
            message.error(t('storeGroup.fetchStoresFailed'));
            setStores([]);
          }
        } else {
          console.log('No shopCodes found, setting empty stores');
          setStores([]);
        }
      } else {
        message.error(response.errorMessage || t('storeGroup.fetchDetailFailed'));
      }
    } catch (error) {
      message.error(t('storeGroup.fetchDetailFailed'));
      console.error('Failed to fetch store group details:', error);
    } finally {
      setLoading(false);
    }
  };

  // 将 warehouse 数据转换为 Store 格式的辅助函数
  const convertWarehouseToStore = (warehouse: any): Store => {
    return {
      id: warehouse.id ? warehouse.id.toString() : '',
      code: warehouse.code || '',
      name: warehouse.name || '',
      detailAddress: warehouse.detailAddress || warehouse.address || '',
      area: warehouse.area || warehouse.region || '',
      city: warehouse.city || '',
      format: warehouse.format || warehouse.type || 'Standard',
      clusterSection: warehouse.clusterSection,
      size: warehouse.size || 0,
      isRepresentative: false,
      contact: warehouse.contactName || warehouse.contact || '',
      phone: warehouse.contactPhone || warehouse.phone || '',
      status: warehouse.status === 'ACTIVE' ? 'ENABLE' :
          warehouse.status === 'INACTIVE' ? 'DISABLE' :
              warehouse.status || 'ENABLE'
    };
  };
  
  const handleEdit = () => {
    navigate(`/store-group/${code}/edit`);
  };

  const handleBack = () => {
    navigate('/store-group/list');
  };

  const handleExportNielsen = () => {
    if (!storeGroup) return;

    confirm({
      title: t('storeGroup.exportNielsenConfirmTitle'),
      icon: <ExportOutlined />,
      content: t('storeGroup.exportNielsenConfirmContent', { name: storeGroup.name }),
      onOk: async () => {
        try {
          const response = await storeGroupService.exportToNielsen(storeGroup.code);
          if (response.success && response.data) {
            message.success(t('storeGroup.exportSuccess'));
            // 模拟下载文件
            const downloadLink = document.createElement('a');
            downloadLink.href = response.data.downloadUrl;
            downloadLink.download = response.data.fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
          } else {
            message.error(response.errorMessage || t('storeGroup.exportFailed'));
            return;
          }
          fetchStoreGroupDetail(code!); // Refresh details to update last export time
        } catch (error: any) {
          message.error(t('storeGroup.exportFailed', { error: error.message }));
        }
      },
    });
  };

  const storeColumns = [
    {
      title: t('storeGroup.code'),
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: t('storeGroup.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('storeGroup.type'),
      dataIndex: 'format',
      key: 'format',
    },
    {
      title: t('storeGroup.size'),
      dataIndex: 'size',
      key: 'size',
    },
    {
      title: t('storeGroup.representativeStore'),
      dataIndex: 'code',
      key: 'representative',
      render: (storeCode: string) => {
        const isRepresentative = storeGroup?.typicalStoreCode === storeCode;
        return isRepresentative ? <Tag color="green">{t('common.yes')}</Tag> : <Tag color="default">{t('common.no')}</Tag>;
      },
    },
    // {
    //   title: t('storeGroup.address'),
    //   dataIndex: 'detailAddress',
    //   key: 'detailAddress',
    //   ellipsis: true,
    // },
  ];

  const formatDateTime = (timestamp: number | string | undefined): string => {
      if (!timestamp) {
        return '-';
      }
      return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

  // 获取状态标签的国际化文本
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ENABLE':
        return t('storeGroup.status.enable');
      case 'DISABLE':
        return t('storeGroup.status.disable');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!storeGroup) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>{t('storeGroup.notFoundOrDeleted')}</h2>
        <Button type="primary" onClick={handleBack}>
          {t('common.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="store-group-detail">
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('common.back')}
          </Button>
          <h1>{t('storeGroup.detailTitle')}</h1>
        </Space>
        <Space>
          <Button icon={<EditOutlined />} onClick={handleEdit}>
            {t('common.edit')}
          </Button>
          <Tooltip title={t('storeGroup.exportNielsenTooltip')}>
            <Button icon={<ExportOutlined />} onClick={handleExportNielsen}>
              {t('storeGroup.exportNielsenButton')}
            </Button>
          </Tooltip>
        </Space>
      </div>

      <Card>
        <Descriptions title={t('storeGroup.basicInfo')} bordered column={2}>
          <Descriptions.Item label={t('storeGroup.code')}>{storeGroup.code}</Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.name')}>{storeGroup.name}</Descriptions.Item>
          <Descriptions.Item label={t('common.status')}>
            <Tag color={STORE_GROUP_STATUS_COLORS[storeGroup.status]}>
              {getStatusLabel(storeGroup.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.relatedDisplayGroup')}>
            {storeGroup.displayGroupCode}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.displayGroupName')}>
            {storeGroup.displayGroupName}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.storeCount')}>
            {storeGroup.shopCodes?.length || 0}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.createBy')}>
            {storeGroup.creatorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.createTime')}>
            {formatDateTime(storeGroup.gmtCreate)}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.lastExportTime')}>
            {formatDateTime(storeGroup.lastExportTime)}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.lastImportTime')}>
            {formatDateTime(storeGroup.lastImportTime)}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.remark')} span={2}>
            {storeGroup.remark || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Approval Information */}
      <Card title={t('storeGroup.approvalInfo.title')} style={{ marginBottom: '16px' }}>
        <div className="approval-sections">
          {/* Commercial Approval */}
          <div className="approval-section">
            <Card
              size="small"
              title={t('storeGroup.approvalInfo.commercialApproval')}
              style={{
                borderLeft: storeGroup.commercialApproverStatus === 'APPROVED' ? '4px solid #52c41a' :
                           storeGroup.commercialApproverStatus === 'REJECTED' ? '4px solid #ff4d4f' :
                           storeGroup.commercialApproverStatus === 'PENDING' ? '4px solid #faad14' : '4px solid #d9d9d9'
              }}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('storeGroup.approvalInfo.status')}>
                   {storeGroup.commercialApproverStatus ? (
                    <Tag color={
                      storeGroup.commercialApproverStatus === 'APPROVED' ? 'green' :
                      storeGroup.commercialApproverStatus === 'REJECTED' ? 'red' :
                      storeGroup.commercialApproverStatus === 'PENDING' ? 'orange' : 'default'
                    }>
                      {storeGroup.commercialApproverStatus === 'APPROVED' ? t('storeGroup.approvalStatus.approved') :
                       storeGroup.commercialApproverStatus === 'REJECTED' ? t('storeGroup.approvalStatus.rejected') :
                       storeGroup.commercialApproverStatus === 'PENDING' ? t('storeGroup.approvalStatus.pending') : storeGroup.commercialApproverStatus}
                    </Tag>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approverName')}>
                  {storeGroup.commercialApproverName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approverAccount')}>
                  {storeGroup.commercialApproverAccount || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approvalTime')}>
                  {formatDateTime(storeGroup.commercialApproverTime)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </div>
          
          {/* Planogram Approval */}
          <div className="approval-section">
            <Card
              size="small"
              title={t('storeGroup.approvalInfo.planogramApproval')}
              style={{
                borderLeft: storeGroup.planogramApproverStatus === 'APPROVED' ? '4px solid #52c41a' :
                           storeGroup.planogramApproverStatus === 'REJECTED' ? '4px solid #ff4d4f' :
                           storeGroup.planogramApproverStatus === 'PENDING' ? '4px solid #faad14' : '4px solid #d9d9d9'
              }}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('storeGroup.approvalInfo.status')}>
                  {storeGroup.planogramApproverStatus ? (
                    <Tag color={
                      storeGroup.planogramApproverStatus === 'APPROVED' ? 'green' :
                      storeGroup.planogramApproverStatus === 'REJECTED' ? 'red' :
                      storeGroup.planogramApproverStatus === 'PENDING' ? 'orange' : 'default'
                    }>
                      {storeGroup.planogramApproverStatus === 'APPROVED' ? t('storeGroup.approvalStatus.approved') :
                       storeGroup.planogramApproverStatus === 'REJECTED' ? t('storeGroup.approvalStatus.rejected') :
                       storeGroup.planogramApproverStatus === 'PENDING' ? t('storeGroup.approvalStatus.pending') : storeGroup.planogramApproverStatus}
                    </Tag>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approverName')}>
                  {storeGroup.planogramApproverName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approverAccount')}>
                  {storeGroup.planogramApproverAccount || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approvalTime')}>
                   {formatDateTime(storeGroup.planogramApproverTime)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </div>

          {/* operation Approval */}
          <div className="approval-section">
            <Card
              size="small"
              title={t('storeGroup.approvalInfo.operationApproval')}
              style={{
                borderLeft: storeGroup.operateApproverStatus === 'APPROVED' ? '4px solid #52c41a' :
                           storeGroup.operateApproverStatus === 'REJECTED' ? '4px solid #ff4d4f' :
                           storeGroup.operateApproverStatus === 'PENDING' ? '4px solid #faad14' : '4px solid #d9d9d9'
              }}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('storeGroup.approvalInfo.status')}>
                  {storeGroup.operateApproverStatus ? (
                    <Tag color={
                      storeGroup.operateApproverStatus === 'APPROVED' ? 'green' :
                      storeGroup.operateApproverStatus === 'REJECTED' ? 'red' :
                      storeGroup.operateApproverStatus === 'PENDING' ? 'orange' : 'default'
                    }>
                      {storeGroup.operateApproverStatus === 'APPROVED' ? t('storeGroup.approvalStatus.approved') :
                       storeGroup.operateApproverStatus === 'REJECTED' ? t('storeGroup.approvalStatus.rejected') :
                       storeGroup.operateApproverStatus === 'PENDING' ? t('storeGroup.approvalStatus.pending') : storeGroup.operateApproverStatus}
                    </Tag>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approverName')}>
                  {storeGroup.operateApproverName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approverAccount')}>
                   {storeGroup.operateApproverAccount || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('storeGroup.approvalInfo.approvalTime')}>
                   {formatDateTime(storeGroup.operateApproverTime)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </div>
        </div>
      </Card>

      <Tabs defaultActiveKey="stores" style={{ marginTop: 16 }}>
        <TabPane tab={t('storeGroup.relatedStores')} key="stores">
          <Table
            rowKey="id"
            columns={storeColumns}
            dataSource={stores}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => t('common.paginationTotal', { total }),
            }}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default StoreGroupDetail;